<template>
	<div class="flex py-3 font-body text-sm text-gray-600 lg:px-10">
		<ul class="m-0 flex list-none items-center p-0">
			<li class="pr-4 lg:pr-6" :class="[hasPrev() ? '' : 'opacity-25']">
				<a
					:href="
						hasPrev() && prevPage > 1
							? currentURL + prevPage
							: paginationLessURL
					"
					@click.prevent="hasPrev() ? changePage(prevPage) : ''"
				>
					<div
						class="flex h-6 w-6 items-center justify-center rounded-md hover:bg-gray-200"
					>
						<div class="flex items-center justify-center">
							<svg
								class="h-4 w-4 text-gray-600"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
						</div>
					</div>
				</a>
			</li>
			<li v-if="hasFirst()" class="pr-4 lg:pr-6">
				<a :href="paginationLessURL" @click.prevent="changePage(1)">
					<div
						class="flex h-6 w-6 items-center justify-center rounded-md text-gray-600 hover:bg-gray-200"
					>
						<span class="">01</span>
					</div>
				</a>
			</li>
			<li v-if="hasFirst() && rangeStart > 2" class="pr-4 lg:pr-6">
				...
			</li>
			<li
				v-for="page in pages"
				:key="page"
				class="pr-4 lg:pr-6"
				:class="[page < rangeStart || page > rangeEnd ? 'hidden' : '']"
			>
				<a
					:href="page === 1 ? paginationLessURL : currentURL + page"
					@click.prevent="changePage(page)"
				>
					<div
						:class="[
							whitelabel?.styles?.text,
							current == page
								? 'hover:bg-transparent'
								: 'text-gray-600 hover:bg-gray-200',
						]"
						class="flex h-6 w-6 items-center justify-center rounded-md text-blue-400"
					>
						<span class="">
							{{ String(page).padStart(2, '0') }}
						</span>
					</div>
				</a>
			</li>
			<li
				v-if="hasLast() && rangeEnd < totalPages - 1"
				class="pr-4 lg:pr-6"
			>
				...
			</li>
			<li v-if="hasLast()" class="pr-4 lg:pr-6">
				<a
					:href="currentURL + totalPages"
					@click.prevent="changePage(totalPages)"
				>
					<div
						class="flex h-6 w-6 items-center justify-center rounded-md text-gray-600 hover:bg-gray-200"
					>
						<span class="">
							{{ String(totalPages).padStart(2, '0') }}
						</span>
					</div>
				</a>
			</li>
			<li class="pr-4 lg:pr-6" :class="[hasNext() ? '' : 'opacity-25']">
				<a
					:href="hasNext() ? currentURL + nextPage : ''"
					@click.prevent="hasNext() ? changePage(nextPage) : ''"
				>
					<div
						class="flex h-6 w-6 items-center justify-center rounded-md hover:bg-gray-200"
					>
						<div class="flex items-center justify-center">
							<svg
								class="h-4 w-4 text-gray-600"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 5l7 7-7 7"
								/>
							</svg>
						</div>
					</div>
				</a>
			</li>
		</ul>
	</div>
</template>

<script setup>
const props = defineProps({
	current: {
		type: Number,
		default: 1,
	},
	total: {
		type: Number,
		default: 0,
	},
	perPage: {
		type: Number,
		default: 9,
	},
	pageRange: {
		type: Number,
		default: 2,
	},
	textBeforeInput: {
		type: String,
		default: 'Go to page',
	},
	textAfterInput: {
		type: String,
		default: 'Go',
	},
})

const emit = defineEmits(['page-changed'])

const route = useRoute()
const { whitelabel } = useWhitelabelsStyles()

const hasFirst = () => rangeStart.value !== 1
const hasLast = () => rangeEnd.value < totalPages.value
const hasPrev = () => props.current > 1
const hasNext = () => props.current < totalPages.value

const changePage = (page) => {
	if (page > 0 && page <= totalPages.value) {
		emit('page-changed', page)
	}
}

const totalPages = computed(() => Math.ceil(props.total / props.perPage))
const nextPage = computed(() => props.current + 1)
const prevPage = computed(() => props.current - 1)

const rangeStart = computed(() => {
	const start = props.current - props.pageRange
	return start > 0 ? start : 1
})

const rangeEnd = computed(() => {
	const end = props.current + props.pageRange
	return end < totalPages.value ? end : totalPages.value
})

const pages = computed(() => {
	const returnPages = []
	const startPage = hasFirst() ? 2 : 1
	const endPage = hasLast() ? totalPages.value - 1 : totalPages.value

	for (let i = startPage; i <= endPage; i += 1) {
		returnPages.push(i)
	}
	return returnPages
})

const paginationLessURL = computed(() => {
	const currentURLPath = route.fullPath
	let newURL = `${currentURLPath}`.replace(/&?page=[\d]*/, '')

	if (newURL.substring(newURL.length - 1) === '?') {
		newURL = newURL.substring(0, newURL.length - 1)
	}
	return newURL
})

const currentURL = computed(() => {
	const pageParam = 'page'
	const paginationLessUrl = paginationLessURL.value

	if (paginationLessUrl.includes('?')) {
		return `${paginationLessUrl}&${pageParam}=`
	}
	return `${paginationLessUrl}?${pageParam}=`
})
</script>
